import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../utils/supabaseClient';
import { authFetch } from '../../utils/authFetch';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://krmmadhubanibackend.onrender.com';

function NotificationTester() {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // Student selection
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [classFilter, setClassFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [availableClasses, setAvailableClasses] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [sendTarget, setSendTarget] = useState('all_devices'); // 'all_devices', 'selected_students'

  const fetchStudents = useCallback(async () => {
    try {
      setLoadingStudents(true);
      const { data, error } = await supabase
        .from('students')
        .select('Rno, Name, Class')
        .eq('Active', true)
        .order('Class', { ascending: true })
        .order('Name', { ascending: true });
      if (error) throw error;
      setStudents(data || []);
      const classes = [...new Set((data || []).map(s => s.Class).filter(Boolean))].sort();
      setAvailableClasses(classes);
    } catch (err) {
      console.error('Error fetching students:', err);
    } finally {
      setLoadingStudents(false);
    }
  }, []);

  useEffect(() => { fetchStudents(); }, [fetchStudents]);

  const filteredStudents = students.filter(s => {
    if (classFilter !== 'all' && s.Class !== classFilter) return false;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      return (s.Name?.toLowerCase() || '').includes(q) || String(s.Rno).includes(q);
    }
    return true;
  });

  const toggleStudent = (rno) => {
    setSelectedStudents(prev => prev.includes(rno) ? prev.filter(r => r !== rno) : [...prev, rno]);
  };

  const selectAllFiltered = () => setSelectedStudents(filteredStudents.map(s => s.Rno));
  const clearSelection = () => setSelectedStudents([]);

  const handleSendNotification = async () => {
    if (!title.trim() || !body.trim()) {
      setResult({ success: false, error: 'Title and body are required' });
      return;
    }
    if (sendTarget === 'selected_students' && selectedStudents.length === 0) {
      setResult({ success: false, error: 'Please select at least one student' });
      return;
    }

    setSending(true);
    setResult(null);
    try {
      let res;
      if (sendTarget === 'all_devices') {
        // Send to all admin devices
        res = await authFetch(`${API_BASE_URL}/api/push-token/test`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title: title.trim(), body: body.trim() }),
        });
      } else {
        // Bulk send to selected students
        res = await authFetch(`${API_BASE_URL}/api/push-token/bulk-send`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: title.trim(),
            body: body.trim(),
            studentRnos: selectedStudents,
          }),
        });
      }
      const data = await res.json();
      setResult(data);
    } catch (err) {
      setResult({ success: false, error: err.message });
    } finally {
      setSending(false);
    }
  };

  const selectedCount = selectedStudents.length;

  return (
    <div style={{ maxWidth: 700, margin: '30px auto', padding: 20 }}>
      <h2 style={{ marginBottom: 6, color: '#2c3e50' }}>Notification Tester</h2>
      <p style={{ color: '#7f8c8d', fontSize: 14, marginBottom: 24 }}>
        Send push notifications to registered mobile app devices
      </p>

      <div style={cardStyle}>
        {/* Title */}
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Title *</label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="e.g. Fee Reminder"
            style={inputStyle}
            disabled={sending}
          />
        </div>

        {/* Body */}
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Body *</label>
          <textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="e.g. Your fee payment is due"
            rows={3}
            style={{ ...inputStyle, resize: 'vertical', minHeight: 70 }}
            disabled={sending}
          />
        </div>

        {/* Send Target Toggle */}
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Send To</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setSendTarget('all_devices')}
              disabled={sending}
              style={{
                ...toggleBtnStyle,
                background: sendTarget === 'all_devices' ? '#3498db' : '#f0f0f0',
                color: sendTarget === 'all_devices' ? '#fff' : '#555',
              }}
            >
              📱 All Admin Devices
            </button>
            <button
              onClick={() => setSendTarget('selected_students')}
              disabled={sending}
              style={{
                ...toggleBtnStyle,
                background: sendTarget === 'selected_students' ? '#3498db' : '#f0f0f0',
                color: sendTarget === 'selected_students' ? '#fff' : '#555',
              }}
            >
              🎓 Select Students {selectedCount > 0 && `(${selectedCount})`}
            </button>
          </div>
        </div>

        {/* Student Selector */}
        {sendTarget === 'selected_students' && (
          <div style={{ marginBottom: 16 }}>
            {/* Filters */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <select
                value={classFilter}
                onChange={e => setClassFilter(e.target.value)}
                style={{ ...inputStyle, width: 'auto', minWidth: 120 }}
                disabled={sending}
              >
                <option value="all">All Classes</option>
                {availableClasses.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search name or roll no..."
                style={{ ...inputStyle, flex: 1 }}
                disabled={sending}
              />
            </div>

            {/* Select All / Clear */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <button onClick={selectAllFiltered} disabled={sending} style={actionBtnStyle}>
                Select All ({filteredStudents.length})
              </button>
              <button onClick={clearSelection} disabled={sending || selectedCount === 0} style={actionBtnStyle}>
                Clear ({selectedCount})
              </button>
            </div>

            {/* Student List */}
            <div style={listContainerStyle}>
              {loadingStudents ? (
                <p style={{ textAlign: 'center', color: '#999', padding: 20 }}>Loading students...</p>
              ) : filteredStudents.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#999', padding: 20 }}>No students found</p>
              ) : (
                filteredStudents.map(s => (
                  <label
                    key={s.Rno}
                    style={{
                      ...studentRowStyle,
                      background: selectedStudents.includes(s.Rno) ? '#eef6ff' : '#fff',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedStudents.includes(s.Rno)}
                      onChange={() => toggleStudent(s.Rno)}
                      disabled={sending}
                      style={{ marginRight: 10 }}
                    />
                    <span style={{ fontWeight: 600, minWidth: 60, color: '#2c3e50' }}>{s.Rno}</span>
                    <span style={{ flex: 1, color: '#34495e' }}>{s.Name}</span>
                    <span style={classBadgeStyle}>{s.Class}</span>
                  </label>
                ))
              )}
            </div>
          </div>
        )}

        {/* Send Button */}
        <button
          onClick={handleSendNotification}
          disabled={sending}
          style={{
            background: sending ? '#95a5a6' : '#3498db',
            color: '#fff', border: 'none', borderRadius: 8,
            padding: '10px 24px', fontSize: 14, fontWeight: 600,
            cursor: sending ? 'not-allowed' : 'pointer',
            width: '100%',
          }}
        >
          {sending
            ? 'Sending...'
            : sendTarget === 'all_devices'
              ? 'Send to All Admin Devices'
              : `Send to ${selectedCount} Student${selectedCount !== 1 ? 's' : ''}`
          }
        </button>

        {/* Result */}
        {result && (
          <div style={{
            marginTop: 14, padding: '10px 14px', borderRadius: 8,
            background: result.success ? '#e8f8f0' : '#fde8e8',
            color: result.success ? '#27ae60' : '#e74c3c',
            fontSize: 13, fontWeight: 500,
          }}>
            {result.success
              ? `✅ ${result.message || `Sent: ${result.sent || 0}, Failed: ${result.failed || 0}`}`
              : `❌ ${result.error}`
            }
          </div>
        )}
      </div>
    </div>
  );
}

const cardStyle = {
  background: '#fff', borderRadius: 12, padding: 24,
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '1px solid #e8e8e8',
};

const labelStyle = {
  display: 'block', fontSize: 13, fontWeight: 600,
  color: '#34495e', marginBottom: 4,
};

const inputStyle = {
  width: '100%', padding: '8px 12px', fontSize: 14,
  border: '1px solid #ddd', borderRadius: 8, outline: 'none',
  boxSizing: 'border-box',
};

const toggleBtnStyle = {
  padding: '8px 16px', borderRadius: 8, border: '1px solid #ddd',
  fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s',
};

const actionBtnStyle = {
  padding: '5px 12px', borderRadius: 6, border: '1px solid #ddd',
  background: '#f8f9fa', fontSize: 12, fontWeight: 500, cursor: 'pointer',
  color: '#555',
};

const listContainerStyle = {
  maxHeight: 260, overflowY: 'auto', border: '1px solid #e8e8e8',
  borderRadius: 8, background: '#fafafa',
};

const studentRowStyle = {
  display: 'flex', alignItems: 'center', padding: '8px 12px',
  borderBottom: '1px solid #f0f0f0', cursor: 'pointer', fontSize: 13,
  transition: 'background 0.15s',
};

const classBadgeStyle = {
  padding: '2px 8px', borderRadius: 4, background: '#eef2ff',
  color: '#4f46e5', fontSize: 11, fontWeight: 600,
};

export default NotificationTester;
